import React from 'react';
import { makeStyles } from '@material-ui/core';

import { Typography } from 'ha/ui/next/Typography';
import List from 'ha/ui/next/List';
import ListItem from 'ha/ui/next/ListItem';
import { useIntl } from 'ha/i18n';
import { getListingPreviewTitle } from 'ha/components/Listing/helpers';
import { Kinds } from 'ha/constants/Room';
import getFirstAvailableDay from 'ha/utils/exclusionDate/firstAvailableDay';

import { Values } from 'ha/constants/Facilities';

import { ListingAttributes } from './types';

interface Props {
  room: ListingAttributes;
}

const useStyles = makeStyles(theme => ({
  info: {
    marginTop: theme.spacing(0.5),
  },
  listItem: {
    padding: 0,
  },
}));

const Info: React.FC<Props> = ({ room }) => {
  const { T, formatDate } = useIntl();
  const classes = useStyles();

  const listingKindLabel = getListingPreviewTitle({
    T,
    kind: Number(room.kind),
    type: Number(room.type),
    bedroomCount: room.bedroomCount || '',
  });
  const isBedroomCountPresented = Boolean(room.bedroomCount);
  const isAptSizePresented =
    Boolean(room.totalSize) || Boolean(room.bedroomSize);
  const isPrivateOrSharedRoom =
    Number(room.kind) === Kinds.PrivateRoom ||
    Number(room.kind) === Kinds.SharedRoom;

  let bedroomTranslation = T('%s Bedrooms', room.bedroomCount);

  if (room.bedroomCount === '0') {
    bedroomTranslation = T('Studio');
  }
  if (room.bedroomCount === '1') {
    bedroomTranslation = `1 ${T('Bedroom')}`;
  }

  const firstAvailableDay = getFirstAvailableDay(
    room.exclusions,
    room.minimalRentalPeriod,
  );

  const listingDatesLabel = firstAvailableDay
    ? T('Available from %(dates)s', {
        dates: formatDate('DD MMM YYYY', firstAvailableDay),
      })
    : false;

  return (
    <div>
      <Typography.H3 component="h2" overflow="ellipsis">
        {T('%(kind)s at %(street)s', {
          kind: listingKindLabel,
          street: room.street,
        })}
      </Typography.H3>
      <List disablePadding className={classes.info}>
        {listingDatesLabel && (
          <ListItem disableGutters className={classes.listItem}>
            <Typography.Caption>{listingDatesLabel}</Typography.Caption>
          </ListItem>
        )}

        <ListItem disableGutters className={classes.listItem}>
          <Typography.Caption>
            {room.bedroomFurnished === Values.Yes
              ? T('Furnished')
              : T('Not furnished')}{' '}
            {isBedroomCountPresented && `• ${bedroomTranslation} `}
            {isAptSizePresented &&
              `• ${
                isPrivateOrSharedRoom ? room.bedroomSize : room.totalSize
              } m²`}
          </Typography.Caption>
        </ListItem>
      </List>
    </div>
  );
};

export default Info;
